import { useEffect, useState } from "react";
import { useAppData } from "../../context/AppContext";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { AppSettings } from "../../types/AppSettings";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { SettingsApi } from "../../api/SettingsApi";
import { StripeApi } from "../../api/StripeApi";
import { Money } from "../../components/Money";

interface Props {}

type StripePrice = {
  price5bananas: number
  price20bananas: number
  price50bananas: number
};

const ManagePaymentsPage = ({}: Props) => {
  const { data, setData } = useAppData();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const [fetchingPrices, setFetchingPrices] = useState(false);
  const [fetchedPrices, setFetchedPrices] = useState<StripePrice | null>(null);

  useEffect(() => {
    if (!!data?.loaded) {
      setLoading(false);
    }
  }, [data?.settings])

  useEffect(() => {
    if (loading || !data?.loaded) {
      return;
    }
    if (data?.user?.role === "admin") {
      return
    }

    toast.error("You don't have access to this page.")
    navigate("/banana-stand")
  }, [data?.user, loading, navigate])

  const toggle = async (key: keyof AppSettings) => {
    const oppositeValue = !data?.settings[key];
    setSavingKey(String(key));
    try {
      const settings = await SettingsApi.setPaymentState(key, oppositeValue);
      setData((data) => ({ ...data, settings }));
    } finally {
      setSavingKey(null);
    }
  };

  const fetchPrices = async () => {
    setFetchingPrices(true);
    setFetchedPrices(null);
    try {
      const prices = await StripeApi.updateStripePrices();
       setFetchedPrices(prices);
    } catch (error: any) {
      toast.error("Failed to fetch Stripe prices.");
    } finally {
      setFetchingPrices(false);
    }
  };

  return (
    <>
      <title>Payment Settings | Tuttobanana</title>
      <meta name="description" content="Payment settings" />
      <div className="min-h-screen bg-gray-100 text-gray-900 px-6 py-20 sm:px-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold mb-8 text-center text-yellow-500">
            Payment Settings
          </h1>

          <div className="max-w-2xl mx-auto space-y-3">
            {(loading || data?.user?.role !== "admin") ? (
              <LoadingSpinner label="Loading..." size="md" />
            ) : (
              <>
                <Toggle
                  checked={data?.settings?.are5bananasPaid ?? false}
                  onChange={() => toggle("are5bananasPaid")}
                  disabled={savingKey === "are5bananasPaid"}
                  label="5 Bananas bunch"
                  sublabel={data?.settings?.are5bananasPaid ? "Paid" : "Free"}
                />
                <Toggle
                  checked={data?.settings?.are20bananasPaid ?? false}
                  onChange={() => toggle("are20bananasPaid")}
                  disabled={savingKey === "are20bananasPaid"}
                  label="20 Bananas bunch"
                  sublabel={data?.settings?.are20bananasPaid ? "Paid" : "Free"}
                />
                <Toggle
                  checked={data?.settings?.are50bananasPaid ?? false}
                  onChange={() => toggle("are50bananasPaid")}
                  disabled={savingKey === "are50bananasPaid"}
                  label="50 Bananas bunch"
                  sublabel={data?.settings?.are50bananasPaid ? "Paid" : "Free"}
                />
                {/* New row: Fetch prices from Stripe */}
                <ActionRow
                  label="Fetch prices from Stripe"
                  sublabel="Pull latest prices from your Stripe account."
                  actionLabel={fetchingPrices ? "Loading..." : "Fetch Prices"}
                  onAction={fetchPrices}
                  disabled={fetchingPrices}
                >
                </ActionRow>

                {/* Info message with fetched prices */}
                {fetchedPrices && 
                  <div className="rounded-xl border border-yellow-200 bg-yellow-50 p-4 text-sm">
                    <div className="font-medium mb-1">Updated Prices</div>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>
                          <span className="font-medium">5 bananas</span>{" "}
                          <span className="tabular-nums">
                            {Money.format(fetchedPrices.price5bananas)}
                          </span>
                        </li>
                        <li>
                          <span className="font-medium">20 bananas</span>{" "}
                          <span className="tabular-nums">
                            {Money.format(fetchedPrices.price20bananas)}
                          </span>
                        </li>
                        <li>
                          <span className="font-medium">50 bananas</span>{" "}
                          <span className="tabular-nums">
                            {Money.format(fetchedPrices.price50bananas)}
                          </span>
                        </li>
                      </ul>
                  </div>
                }
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

const Toggle = ({
  checked,
  onChange,
  disabled,
  label,
  sublabel,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  label: string;
  sublabel?: string;
}) => (
  <label className="flex w-full items-center justify-between rounded-xl shadow p-6 bg-white px-4 py-3 transition cursor-pointer disabled:cursor-not-allowed disabled:opacity-60">
    <div className="text-left">
      <div className="font-medium">{label}</div>
      {sublabel && <div className="text-sm text-gray-500">{sublabel}</div>}
    </div>

    <div
      className={`group relative inline-flex w-11 shrink-0 rounded-full p-0.5 transition-colors duration-200 ease-in-out
        ${checked ? "bg-yellow-500" : "bg-gray-200"}
        ${disabled ? "opacity-50" : ""}`}
    >
      <span
        className={`size-5 rounded-full bg-white shadow ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out
          ${checked ? "translate-x-5" : "translate-x-0"}`}
      ></span>
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        aria-label={label}
        className="absolute inset-0 appearance-none focus:outline-none sr-only cursor-pointer"
      />
    </div>
  </label>
);

const ActionRow = ({
  label,
  sublabel,
  actionLabel,
  onAction,
  disabled,
  children,
}: {
  label: string;
  sublabel?: string;
  actionLabel: string;
  onAction: () => void;
  disabled?: boolean;
  children?: React.ReactNode;
}) => (
  <div className="flex w-full items-center justify-between rounded-xl shadow p-6 bg-white px-4 py-3">
    <div className="text-left">
      <div className="font-medium">{label}</div>
      {sublabel && <div className="text-sm text-gray-500">{sublabel}</div>}
    </div>

    <button
      onClick={onAction}
      disabled={disabled}
      className={`inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-medium bg-yellow-500 text-white hover:brightness-95 active:brightness-90 ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
    >
      {actionLabel}
      {children}
    </button>
  </div>
);

export default ManagePaymentsPage;
